import { computed, DestroyRef, inject, Injector, INJECTOR, signal, Signal, untracked, WritableSignal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AsyncSubject, combineLatest, debounceTime, map, multicast, Observable, ObservableInput, of, publishReplay, repeat, share, shareReplay, skipWhile, Subject, Subscription, switchMap, take, tap, throttleTime, throwError } from 'rxjs';
import { AppInjector } from 'src/app/app-injector.module';
import { AppError, catchAppError, isNullOrUndefined } from 'src/app/shared/utils';
import { logger } from './logger';

const LOGGER = logger('Operation');

export type ObservableFactory<T, P extends Array<any> = any[]> = (...args: P) => ObservableInput<T>;
export type OperationProducer<T = any, P extends Array<any> = any[]> = ObservableInput<T> | ObservableFactory<T, P>;

export type OperationOptions<T> = {
  readonly debugName?: string;
  
  readonly injector?: Injector;

  /** 
   * Operation will run until destroyRef is completed.
   * If undefined, will use injected DestroyRef via `injector` or `AppInjector`.
   **/
  readonly destroyRef?: DestroyRef;

  /** 
   * Computation that will trigger this operation.
   * The callback must be reactive.
   * The operation will respect `debounce` and `throttle`.
   **/
  readonly when?: () => boolean;

  /** Configures error handling. */
  readonly error?: {
    /** 
     * Show snack on error?
     * Defaults to `true`.
     **/
    readonly snack?: boolean;

    /** 
     * Override snack message. 
     * Literal or formatting callback taking the `AppError`.
     **/
    readonly message?: string | ((error: AppError) => string);
  };
} & (
    { readonly debounce?: number; } |
    { readonly throttle?: number; }
) & (
  {
    /**
     * Whether the operation must succeed before accessing its value.
     * When `true`, attempting to access `operation.value()` will throw an error
     * if the operation hasn't ran or is currently running.
     * Defaults to `false`.
     **/
    readonly mustSucceed?: true;
  } | 
  {
    /**
     * Value returned by `operation.value()` until the operation has completed at least once.
     * Defaults to `undefined`.
     **/
    readonly initialValue?: T | undefined;
  }
);

export interface OperationLike<T, P extends Array<any> = any[]> {
  /** 
   * Options this operation has been created with. 
   **/
  readonly opts: OperationOptions<T>;

  
  /** 
   * Whether this operation is pending to run again.
   * The operation will stop being pending once it has started. */
  readonly pending: Signal<boolean>;
  

  /**
   * Whether this operation has started.
   * The opeartion will remain started following the first run.
   */
  readonly started: Signal<boolean>;
  
  /** 
   * Time at which latest run of this operation started. 
   * Unlike `started` this is updated on every run's start.
   */
  readonly startedAt: Signal<Date>
  

  /**
   * Whether this operation is running.
   * This is equivalent to `pending() || (started() && !completed())`.
   **/
  readonly running: Signal<boolean>;
  

  /**
   * Whether this operation has completed the latest started run.
   **/
  readonly completed: Signal<boolean>;
  
  /** Time at which latest run of this operation completed. */
  readonly completedAt: Signal<Date>;

  
  /** Whether the latest run of this operation has succeeded. */
  readonly succeeded: Signal<boolean>;
  
  /** Whether the latest run of this operation has failed. */
  readonly failed: Signal<boolean>;
  
  /**
   * Error from the latest run of this operation. 
   * Undefined if the operation has not failed.
   */
  readonly error: Signal<AppError | undefined>


  /** 
   * Value produced by the latest run of this operation.
   * Returns `initialValue` if the operation has not completed at least once.
   * If `mustRun` is `true`, throws an error if the operation hasn't ran or is running.
   **/
  readonly value: Signal<T | undefined>


  /** 
   * Returns an observable that emits when a run of this operation completes.
   * The emitted value indicates whether the run succeeded or failed. */
  readonly completed$: Observable<boolean>;

  /**
   * Returns an observable that emits when a run of this operation
   * completes or fails.
   **/
  readonly value$: Observable<T>;


  /** Queues run of this operation. */
  (...args: P): Operation<T>;
}

export class Operation<T> {
  private _debugName: string;
  private _injector: Injector;
  private _destroyRef: DestroyRef;
  private _value: WritableSignal<T>;
  private _runSubject = new Subject<any[]>();
  private _sub: Subscription;
  private _destroyed = false;

  readonly pending = signal(false);

  readonly started = signal(false);
  readonly startedAt = signal<Date>(undefined);

  readonly running = computed(() => this.pending() || (this.started() && !this.completed()));

  readonly completed = computed(() => this.succeeded() || this.failed());
  readonly completedAt = signal<Date>(undefined);
  
  readonly succeeded = signal(false);
  readonly failed = signal(false);
  readonly error = signal<AppError>(undefined);

  readonly value: Signal<T>;

  readonly completed$: Observable<boolean>;
  readonly value$: Observable<T>;

  constructor(
    readonly producer: any,
    readonly opts: OperationOptions<T>) { 
    this._debugName = isNullOrUndefined(opts.debugName) ? 'operation' : opts.debugName;
    this._injector = opts.injector ?? inject(INJECTOR, { optional: true }) ?? AppInjector;
    this._destroyRef = opts.destroyRef ?? this._injector.get(DestroyRef, null, { optional: true });

    if (!this._destroyRef) 
      LOGGER.warn(`${this._debugName} must be destroyed manually as it has not been provided a DestroyRef`);

    let initialValue = 'initialValue' in opts ? opts.initialValue : undefined;
    this._value = signal(initialValue);


    let mustSucceed = 'mustSucceed' in opts && opts.mustSucceed;
    if (mustSucceed) {
      this.value = computed(() => {
        untracked(() => {
          if (!this.started())
            throw Error(`Cannot read value of ${this._debugName}, because it has not started yet.`);

          if (this.running())
            throw Error(`Cannot read value of ${this._debugName}, because it is running.`);

          if (this.failed()) {
            let error = this.error();
            throw Error(`Cannot read value of ${this._debugName}, because it has failed.`, { cause: error });
          }
        });

        return this._value();
      });
    }
    else {
      this.value = computed(() => {
        untracked(() => {
          if (this.failed()) {
            let error = this.error();
            throw error;
          }
        });

        return this._value();
      });
    }


    let source$ = this._runSubject.asObservable();

    if (this._destroyRef) 
      source$.pipe(takeUntilDestroyed(this._destroyRef));

    if ('debounce' in opts) {
      source$ = source$.pipe(debounceTime(opts.debounce));
    }
    else if ('throttle' in opts) {
      source$ = source$.pipe(throttleTime(opts.throttle));
    }

    let producerFactory: Function = 
      typeof producer !== 'function'
        ? (...args: any[]) => this.producer
        : this.producer;

    let completedSubject = new Subject<boolean>();
    let valueSubject = new Subject<any>();

    source$ = source$.pipe(
      tap(() => this.start()),
      switchMap(args => {
        let sourceObservable = <Observable<T>>producerFactory(...args) ?? of(this._value());
        return sourceObservable.pipe(
          map(v => { 
            this.succeed(v)
            completedSubject.next(true);
            valueSubject.next(v);
            return [null, v];
          }),
          catchAppError((err: AppError) => {
            this.fail(err);
            completedSubject.next(false);
            valueSubject.next(undefined);
            return of([err, this._value()]);
          }, {
            showErrorSnack: !!this.opts?.error?.snack, 
            errorMessage: this.opts?.error?.message 
          }),
        );
      })
    );

    this.completed$ = completedSubject;
    this.value$ = valueSubject;

    this._sub = new Subscription();
    this._sub.add(source$.subscribe());

    this._destroyRef?.onDestroy(() => {
      this.destroy();
    });
  }
  
  destroy() {
    if (this._destroyed)
      return;

    this._destroyed = true;

    this.complete();
    this._runSubject.complete();
    this._sub?.unsubscribe();
  }

  run(...args: any[]) {
    if (this._destroyed)
      throw new Error(`${this._debugName} is destroyed`);

    this.pending.set(true);
    this.succeeded.set(false);
    this.error.set(undefined);
    this.failed.set(false);

    this._runSubject.next(args ?? []);
    return this;
  }

  private start() {
    LOGGER.debug(`${this._debugName} started.`, { operation: this });
    this.pending.set(false);

    this.startedAt.set(new Date());
    this.started.set(true);
  }

  private succeed(value: T) {
    LOGGER.debug(`${this._debugName} succeeded.`, { operation: this });
    
    this.complete();
   
    this._value.set(value);
    this.succeeded.set(true);
  }

  private fail(error: AppError) {
    LOGGER.error(`${this._debugName} thrown an error.`, { operation: this }, error);
    
    this.complete();
    
    this._value.set(undefined);
    this.error.set(error);
    this.failed.set(true);
  }

  private complete() {
    this.completedAt.set(new Date());

    this.pending.set(false);
  }
}

export const defaultOperationOptions: OperationOptions<any> = {
  error: { snack: true }
};

export function operation<T = any>(producer: ObservableInput<T>): OperationLike<T>;
export function operation<T = any>(producer: ObservableInput<T>, debugName: string): OperationLike<T>;
export function operation<T = any>(producer: ObservableInput<T>, opts: OperationOptions<T>): OperationLike<T>;
export function operation<T = any, P extends Array<any> = any[]>(producer: ObservableFactory<T, P>): OperationLike<T, P>;
export function operation<T = any, P extends Array<any> = any[]>(producer: ObservableFactory<T, P>, debugName: string): OperationLike<T, P>;
export function operation<T = any, P extends Array<any> = any[]>(producer: ObservableFactory<T, P>, opts: OperationOptions<T>): OperationLike<T, P>;
export function operation<T = any, P extends Array<any> = any[]>(producer: OperationProducer<T, P>, args?: any): OperationLike<T, P> {
  let opts = isNullOrUndefined(args) 
    ? defaultOperationOptions
    : typeof args === 'string' ? { debugName: args }
    : typeof args === 'object' ? {
      ...defaultOperationOptions,
      ...args, 
      error: { ...defaultOperationOptions.error, ...args.error }
    } 
    : defaultOperationOptions;

  let operation = new Operation<T>(producer, opts);

  let fn = (...args: any[]) => operation.run(...args);
  Object.assign(fn, operation);
  return <OperationLike<T>>fn;
}
